/**
 * Supabase Service - Real API layer for Decixa Web App
 * Location: src/services/supabase/index.ts
 * All data access for the app goes through these functions.
 */

import { getSupabaseClient, getCurrentSession } from './client';
import { env } from '@/config/env';
import { queryClient } from '@/config/queryClient';
import { useAuthStore } from '@/store';
import type {
  User,
  Category,
  Decision,
  Poll,
  Comment,
  Group,
  GroupMember,
  FeedItem,
  VoteType,
  CreateDecisionInput,
  CreateCommentInput,
  CreateGroupInput,
  CreatePollInput,
  Notification,
  NotificationType
} from '@/types';

// ============================================
// HELPERS
// ============================================
const mapProfile = (profile: any): User => ({
  id: profile.id,
  email: profile.email,
  username: profile.username,
  displayName: profile.display_name,
  avatar: profile.avatar_url,
  bio: profile.bio,
  isVerified: profile.is_verified,
  tagline: profile.tagline,
  city: profile.city,
  country: profile.country,
  decisionsCount: profile.decisions_count || 0,
  votesCount: profile.votes_count || 0,
  commentsCount: profile.comments_count || 0,
  helpfulCount: profile.helpful_count || 0,
  followersCount: profile.followers_count || 0,
  followingCount: profile.following_count || 0,
  impactScore: profile.impact_score || 0,
  currentStreak: profile.current_streak || 0,
  longestStreak: profile.longest_streak || 0,
  createdAt: profile.created_at,
});

const mapCategory = (category: any): Category => ({
  id: category.id,
  name: category.name,
  slug: category.slug,
  emoji: category.emoji,
  color: category.color,
  description: category.description,
});

const mapDecision = (d: any, userVote?: VoteType, isBookmarked?: boolean): Decision => ({
  id: d.id,
  userId: d.user_id,
  title: d.title,
  description: d.description || '',
  category: d.categories ? mapCategory(d.categories) : { id: '', name: '', slug: '', emoji: '', color: '' },
  user: d.profiles ? mapProfile(d.profiles) : {} as User,
  isAnonymous: d.is_anonymous,
  yesCount: d.yes_count || 0,
  noCount: d.no_count || 0,
  maybeCount: d.maybe_count || 0,
  totalVotes: d.total_votes || 0,
  commentsCount: d.comments_count || 0,
  yesLabel: d.yes_label || 'Yes',
  noLabel: d.no_label || 'No',
  maybeLabel: d.maybe_label || '',
  userVote,
  isBookmarked,
  group: d.groups ? { id: d.groups.id, name: d.groups.name, icon: d.groups.icon } : undefined,
  expiresAt: d.expires_at || undefined,
  createdAt: d.created_at,
  updatedAt: d.updated_at,
});

const mapGroup = (g: any, membership?: any, pending?: boolean): Group => (
  {
    id: g.id,
    name: g.name,
    slug: g.slug,
    description: g.description || '',
    icon: g.icon || '👥',
    bannerUrl: g.banner_url,
    category: g.categories ? mapCategory(g.categories) : { id: '', name: '', slug: '', emoji: '', color: '' },
    isPrivate: g.is_private,
    rules: g.rules,
    membersCount: g.members_count || 0,
    decisionsCount: g.decisions_count || 0,
    pollsCount: g.polls_count || 0,
    owner: g.profiles ? mapProfile(g.profiles) : {} as User,
    isMember: !!membership,
    isAdmin: membership?.role === 'admin' || membership?.role === 'owner',
    isPending: !!pending,
    userRole: membership?.role,
    createdAt: g.created_at,
  }
);

const mapComment = (c: any): Comment => ({
  id: c.id,
  text: c.text,
  user: c.profiles ? mapProfile(c.profiles) : {} as User,
  isHelpful: c.is_helpful || false,
  helpfulCount: c.helpful_count || 0,
  repliesCount: c.replies_count || 0,
  isMarkedHelpfulByAuthor: c.is_helpful || false,
  decisionId: c.decision_id,
  pollId: c.poll_id,
  parentCommentId: c.parent_comment_id,
  createdAt: c.created_at,
  replies: c.replies?.map(mapComment),
});

// ============================================
// CURRENT USER
// ============================================
async function getCurrentUserId(): Promise<string | null> {
  // Await getSession() so all callers act as a sync barrier: on a fresh page
  // load the Supabase client restores its session asynchronously from
  // storage, and queries that fired before that restore completed had no auth
  // token attached, so RLS silently returned empty rows. No fallback to the
  // Zustand-cached id: if there's no session, writes would fail RLS anyway —
  // better to surface "Not authenticated" than a confusing DB error.
  const { data: { session } } = await getSupabaseClient().auth.getSession();
  return session?.user?.id ?? null;
}

export function CURRENT_USER_ID(): string | null {
  const { user } = useAuthStore.getState();
  return user?.id || null;
}

// ============================================
// BLOCKED USERS
// ============================================
// Short-lived cache of the ids the current user has blocked, so content
// queries (feed, comments, trending) can filter them out without an extra
// round-trip on every call. Invalidated on block/unblock and per-user.
let blockedIdsCache: { userId: string; ids: Set<string>; fetchedAt: number } | null = null;
const BLOCKED_IDS_TTL_MS = 60_000;

export function invalidateBlockedIdsCache(): void {
  blockedIdsCache = null;
}

async function getBlockedUserIds(userId: string | null): Promise<Set<string>> {
  if (!userId) return new Set();

  const now = Date.now();
  if (
    blockedIdsCache &&
    blockedIdsCache.userId === userId &&
    now - blockedIdsCache.fetchedAt < BLOCKED_IDS_TTL_MS
  ) {
    return blockedIdsCache.ids;
  }

  const { data, error } = await getSupabaseClient()
    .from('blocks')
    .select('blocked_id')
    .eq('blocker_id', userId);

  if (error) {
    console.error('Error fetching blocked user ids:', error);
    return blockedIdsCache?.userId === userId ? blockedIdsCache.ids : new Set();
  }

  const ids = new Set<string>((data || []).map(b => b.blocked_id));
  blockedIdsCache = { userId, ids, fetchedAt: now };
  return ids;
}

// ============================================
// DECISION SERVICE
// ============================================
export async function getDecisions(options?: {
  categoryId?: string;
  userId?: string;
  groupId?: string;
  limit?: number;
  offset?: number;
}): Promise<Decision[]> {
  const userId = await getCurrentUserId();
  const supabase = getSupabaseClient();

  let query = supabase
    .from('decisions')
    .select(`
      *,
      profiles!user_id(*),
      categories!category_id(*),
      groups!group_id(id, name, icon)
    `)
    .eq('is_active', true)
    .order('created_at', { ascending: false });

  if (options?.categoryId) {
    query = query.eq('category_id', options.categoryId);
  }
  if (options?.userId) {
    query = query.eq('user_id', options.userId);
  }
  if (options?.groupId) {
    query = query.eq('group_id', options.groupId);
  }

  const limit = options?.limit || 20;
  const offset = options?.offset || 0;
  query = query.range(offset, offset + limit - 1);

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  console.log('[Decixa][getDecisions] fetched:', data?.length ?? 0, 'userId:', userId);

  // Hide content from users the viewer has blocked
  const blockedIds = await getBlockedUserIds(userId);
  const visible = (data || []).filter(d => !blockedIds.has(d.user_id));
  console.log('[Decixa][getDecisions] blocked:', blockedIds.size, 'visible:', visible.length);

  // Get user votes and bookmarks
  const userVotes: Record<string, VoteType> = {};
  const userBookmarks: Set<string> = new Set();

  if (userId && visible.length > 0) {
    const decisionIds = visible.map(d => d.id);

    const [votesRes, bookmarksRes] = await Promise.all([
      getSupabaseClient().from('decision_votes').select('decision_id, vote').eq('user_id', userId).in('decision_id', decisionIds),
      getSupabaseClient().from('bookmarks').select('decision_id').eq('user_id', userId).in('decision_id', decisionIds),
    ]);

    votesRes.data?.forEach(v => { userVotes[v.decision_id] = v.vote as VoteType; });
    bookmarksRes.data?.forEach(b => { userBookmarks.add(b.decision_id); });
  }

  return visible.map(d => mapDecision(d, userVotes[d.id], userBookmarks.has(d.id)));
}

export async function getDecisionById(id: string): Promise<Decision | null> {
  if (!id) {
    console.error('getDecisionById: ID is required');
    return null;
  }

  const userId = await getCurrentUserId();

  const { data, error } = await getSupabaseClient()
    .from('decisions')
    .select(`
      *,
      profiles!user_id(*),
      categories!category_id(*),
      groups!group_id(id, name, icon)
    `)
    .eq('id', id)
    .single();

  if (error || !data) return null;

  let userVote: VoteType | undefined;
  let isBookmarked = false;

  if (userId) {
    const [voteRes, bookmarkRes] = await Promise.all([
      getSupabaseClient().from('decision_votes').select('vote').eq('decision_id', id).eq('user_id', userId).maybeSingle(),
      getSupabaseClient().from('bookmarks').select('id').eq('decision_id', id).eq('user_id', userId).maybeSingle(),
    ]);

    userVote = voteRes.data?.vote as VoteType | undefined;
    isBookmarked = !!bookmarkRes.data;
  }

  return mapDecision(data, userVote, isBookmarked);
}

export async function createDecision(input: CreateDecisionInput): Promise<Decision> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');
  const supabase = getSupabaseClient();

  // Validation
  if (!input.title?.trim()) {
    throw new Error('Title is required');
  }
  if (input.title.length > 150) {
    throw new Error('Title must be 150 characters or less');
  }
  if (!input.categoryId) {
    throw new Error('Category is required');
  }

  const { data, error } = await supabase
    .from('decisions')
    .insert({
      user_id: userId,
      title: input.title.trim(),
      description: input.description?.trim() || '',
      category_id: input.categoryId,
      yes_label: input.yesLabel?.trim() || 'Yes',
      no_label: input.noLabel?.trim() || 'No',
      maybe_label: input.maybeLabel?.trim() || '',
      is_anonymous: input.isAnonymous || false,
      group_id: input.groupId || null,
      expires_at: input.expiresAt || null,
    })
    .select(`
      *,
      profiles!user_id(*),
      categories!category_id(*),
      groups!group_id(id, name, icon)
    `)
    .single();

  if (error) throw new Error(error.message);
  return mapDecision(data);
}

export async function voteOnDecision(decisionId: string, vote: VoteType): Promise<Decision> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  if (!decisionId) {
    throw new Error('Decision ID is required');
  }
  if (!['yes', 'no', 'maybe'].includes(vote)) {
    throw new Error('Invalid vote type');
  }


  const { error } = await getSupabaseClient()
    .from('decision_votes')
    .insert({ decision_id: decisionId, user_id: userId, vote });

  if (error) throw new Error(error.message);

  const decision = await getDecisionById(decisionId);
  if (!decision) throw new Error('Decision not found');
  return decision;
}

export async function bookmarkDecision(decisionId: string, bookmark: boolean): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  if (!decisionId) {
    throw new Error('Decision ID is required');
  }

  if (bookmark) {
    const { error } = await getSupabaseClient().from('bookmarks').insert({ user_id: userId, decision_id: decisionId });
    if (error && error.code !== '23505') throw new Error(error.message); // Ignore duplicate
  } else {
    const { error } = await getSupabaseClient().from('bookmarks').delete().eq('user_id', userId).eq('decision_id', decisionId);
    if (error) throw new Error(error.message);
  }
}

export async function bookmarkPoll(pollId: string, bookmark: boolean): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  if (!pollId) {
    throw new Error('Poll ID is required');
  }

  if (bookmark) {
    const { error } = await getSupabaseClient().from('bookmarks').insert({ user_id: userId, poll_id: pollId });
    if (error && error.code !== '23505') throw new Error(error.message); // Ignore duplicate
  } else {
    const { error } = await getSupabaseClient().from('bookmarks').delete().eq('user_id', userId).eq('poll_id', pollId);
    if (error) throw new Error(error.message);
  }
}

export async function getBookmarks(): Promise<FeedItem[]> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  const supabase = getSupabaseClient();

  // Fetch all bookmarks for the user
  const { data: bookmarks, error } = await supabase
    .from('bookmarks')
    .select('decision_id, poll_id, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  if (!bookmarks || bookmarks.length === 0) return [];

  const decisionIds = bookmarks.filter(b => b.decision_id).map(b => b.decision_id!);
  const pollIds = bookmarks.filter(b => b.poll_id).map(b => b.poll_id!);

  const feed: FeedItem[] = [];

  // Fetch bookmarked decisions
  if (decisionIds.length > 0) {
    const { data: decisions } = await supabase
      .from('decisions')
      .select(`
        *,
        profiles!user_id(*),
        categories!category_id(*),
        groups!group_id(id, name, icon)
      `)
      .in('id', decisionIds);

    // Fetch user votes for these decisions
    const { data: votes } = await supabase
      .from('decision_votes')
      .select('decision_id, vote')
      .eq('user_id', userId)
      .in('decision_id', decisionIds);

    const userVotes: Record<string, VoteType> = {};
    votes?.forEach(v => { userVotes[v.decision_id] = v.vote as VoteType; });

    (decisions || []).forEach(d => {
      feed.push({
        type: 'decision',
        data: mapDecision(d, userVotes[d.id], true), // always bookmarked
      });
    });
  }

  // Fetch bookmarked polls
  if (pollIds.length > 0) {
    const { data: polls } = await supabase
      .from('polls')
      .select(`
        *,
        profiles!user_id(*),
        categories!category_id(*),
        poll_options(*)
      `)
      .in('id', pollIds);

    // Fetch user votes for these polls
    const userVotesMap: Record<string, string[]> = {};
    const { data: pollVotes } = await supabase
      .from('poll_votes')
      .select('poll_id, option_id')
      .eq('user_id', userId)
      .in('poll_id', pollIds);

    pollVotes?.forEach((v: any) => {
      if (!userVotesMap[v.poll_id]) userVotesMap[v.poll_id] = [];
      userVotesMap[v.poll_id]!.push(v.option_id);
    });

    (polls || []).forEach(p => {
      feed.push({
        type: 'poll',
        data: {
          id: p.id,
          userId: p.user_id,
          question: p.title,
          description: p.description,
          category: p.categories ? mapCategory(p.categories) : undefined,
          user: p.profiles ? mapProfile(p.profiles) : ({} as User),
          isAnonymous: p.is_anonymous,
          allowMultiple: p.allow_multiple,
          options: (p.poll_options || []).map((o: any) => ({
            id: o.id,
            text: o.text,
            votesCount: o.votes_count || 0,
            percentage: p.total_votes > 0 ? Math.round((o.votes_count / p.total_votes) * 100) : 0,
          })),
          totalVotes: p.total_votes || 0,
          expiresAt: p.expires_at || undefined,
          createdAt: p.created_at,
          group: undefined,
          aiInsight: undefined,
          userVotedOptionIds: userVotesMap[p.id] || [],
          isBookmarked: true, // always bookmarked
        },
      });
    });
  }

  // Sort by bookmark created_at order (map from original bookmarks order)
  const orderMap = new Map<string, number>();
  bookmarks.forEach((b, i) => {
    const id = b.decision_id || b.poll_id;
    if (id) orderMap.set(id, i);
  });
  feed.sort((a, b) => (orderMap.get(a.data.id) ?? 999) - (orderMap.get(b.data.id) ?? 999));

  return feed;
}

export async function deleteDecision(decisionId: string): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  if (!decisionId) {
    throw new Error('Decision ID is required');
  }

  // Verify ownership
  const { data: decision } = await getSupabaseClient()
    .from('decisions')
    .select('user_id')
    .eq('id', decisionId)
    .single();

  if (!decision) throw new Error('Decision not found');
  if (decision.user_id !== userId) throw new Error('You can only delete your own decisions');

  const { error } = await getSupabaseClient().from('decisions').delete().eq('id', decisionId);
  if (error) throw new Error(error.message);
}

// ============================================
// COMMENT SERVICE
// ============================================

/**
 * Helper function to nest replies under their parent comments (limited to 1 level)
 * - Top-level comments stay at root
 * - Direct replies nest under their parent
 * - Deeper replies are flattened to the first-level parent
 */
function nestCommentReplies(comments: Comment[]): Comment[] {
  const commentMap = new Map<string, Comment>();
  const topLevelComments: Comment[] = [];
  const topLevelIds = new Set<string>();

  // First pass: identify top-level comments and map all comments by ID
  for (const comment of comments) {
    commentMap.set(comment.id, { ...comment, replies: [] });
    if (!comment.parentCommentId) {
      topLevelIds.add(comment.id);
    }
  }

  // Helper to find the top-level ancestor for any comment
  function findTopLevelParent(commentId: string): string | null {
    const comment = commentMap.get(commentId);
    if (!comment) return null;
    if (!comment.parentCommentId) return commentId; // It's a top-level comment
    if (topLevelIds.has(comment.parentCommentId)) return comment.parentCommentId;
    // Recurse up the chain
    return findTopLevelParent(comment.parentCommentId);
  }

  // Second pass: nest replies (max 1 level deep)
  for (const comment of comments) {
    const mappedComment = commentMap.get(comment.id)!;
    if (comment.parentCommentId) {
      // Find the top-level parent to nest under
      const topLevelParentId = findTopLevelParent(comment.id);
      if (topLevelParentId) {
        const topLevelParent = commentMap.get(topLevelParentId);
        if (topLevelParent) {
          topLevelParent.replies = topLevelParent.replies || [];
          topLevelParent.replies.push(mappedComment);
        }
      }
    } else {
      topLevelComments.push(mappedComment);
    }
  }

  return topLevelComments;
}

export async function getComments(decisionId: string): Promise<Comment[]> {
  if (!decisionId) {
    console.error('getComments: Decision ID is required');
    return [];
  }

  // Fetch ALL comments for this decision (including replies)
  const { data, error } = await getSupabaseClient()
    .from('comments')
    .select(`
      *,
      profiles!comments_user_fk(*)
    `)
    .eq('decision_id', decisionId)
    .order('created_at', { ascending: true });

  if (error) throw new Error(error.message);

  const blockedIds = await getBlockedUserIds(await getCurrentUserId());
  const allComments = (data || [])
    .filter(c => !blockedIds.has(c.user_id))
    .map(mapComment);
  return nestCommentReplies(allComments);
}

export async function getPollComments(pollId: string): Promise<Comment[]> {
  if (!pollId) {
    console.error('getPollComments: Poll ID is required');
    return [];
  }

  // Fetch ALL comments for this poll (including replies)
  const { data, error } = await getSupabaseClient()
    .from('comments')
    .select(`
      *,
      profiles!comments_user_fk(*)
    `)
    .eq('poll_id', pollId)
    .order('created_at', { ascending: true });

  if (error) throw new Error(error.message);

  const blockedIds = await getBlockedUserIds(await getCurrentUserId());
  const allComments = (data || [])
    .filter(c => !blockedIds.has(c.user_id))
    .map(mapComment);
  return nestCommentReplies(allComments);
}

export async function createComment(input: CreateCommentInput): Promise<Comment> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  // Validation
  if (!input.text?.trim()) {
    throw new Error('Comment text is required');
  }
  if (input.text.length > 1000) {
    throw new Error('Comment must be 1000 characters or less');
  }
  if (!input.decisionId && !input.pollId) {
    throw new Error('Decision ID or Poll ID is required');
  }

  const { data, error } = await getSupabaseClient()
    .from('comments')
    .insert({
      user_id: userId,
      text: input.text.trim(),
      decision_id: input.decisionId || null,
      poll_id: input.pollId || null,
      parent_comment_id: input.parentCommentId || null,
    })
    .select(`*, profiles!user_id(*)`)
    .single();

  if (error) throw new Error(error.message);
  return mapComment(data);
}

export async function markCommentHelpful(commentId: string): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  if (!commentId) {
    throw new Error('Comment ID is required');
  }

  const { error } = await getSupabaseClient().from('comment_helpful').insert({ comment_id: commentId, user_id: userId });
  if (error && error.code !== '23505') throw new Error(error.message); // Ignore duplicate
}

// ============================================
// GROUP SERVICE
// ============================================
export async function getGroups(options?: {
  categoryId?: string;
  search?: string;
  joined?: boolean;
  limit?: number;
}): Promise<Group[]> {
  const userId = await getCurrentUserId();
  const supabase = getSupabaseClient();

  let query = supabase
    .from('groups')
    .select(`
      *,
      profiles!owner_id(*),
      categories!category_id(*)
    `)
    .eq('is_active', true)
    .order('members_count', { ascending: false });

  if (options?.categoryId) {
    query = query.eq('category_id', options.categoryId);
  }
  if (options?.search?.trim()) {
    const s = options.search.trim();
    query = query.or(`name.ilike.%${s}%,description.ilike.%${s}%`);
  }

  const limit = options?.limit || 20;
  query = query.limit(limit);

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  // Get memberships
  const memberships: Record<string, any> = {};
  if (userId && data) {
    const groupIds = data.map(g => g.id);
    const { data: memberData } = await supabase
      .from('group_members')
      .select('group_id, role')
      .eq('user_id', userId)
      .in('group_id', groupIds);

    memberData?.forEach(m => { memberships[m.group_id] = m; });
  }

  // Get pending requests for current user (if any)
  const pendingRequests: Record<string, boolean> = {};
  if (userId && data) {
    const groupIds = data.map(g => g.id);
    const { data: reqData } = await supabase
      .from('group_requests')
      .select('group_id')
      .eq('user_id', userId)
      .in('group_id', groupIds);
    reqData?.forEach((r: any) => { pendingRequests[r.group_id] = true; });
  }

  let groups = (data || []).map(g => mapGroup(g, memberships[g.id], pendingRequests[g.id]));

  if (options?.joined !== undefined) {
    groups = groups.filter(g => g.isMember === options.joined);
  }

  return groups;
}

export async function getGroupById(id: string): Promise<Group | null> {
  if (!id) {
    console.error('getGroupById: ID is required');
    return null;
  }

  const userId = await getCurrentUserId();
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('groups')
    .select(`
      *,
      profiles!owner_id(*),
      categories!category_id(*)
    `)
    .eq('id', id)
    .single();

  if (error || !data) return null;

  let membership: any = null;
  if (userId) {
    const { data: memberData } = await getSupabaseClient()
      .from('group_members')
      .select('role')
      .eq('group_id', id)
      .eq('user_id', userId)
      .single();
    membership = memberData;
  }

  // Check for pending request by current user
  let isPending = false;
  if (userId) {
    const { data: requestData } = await getSupabaseClient()
      .from('group_requests')
      .select('id')
      .eq('group_id', id)
      .eq('user_id', userId)
      .single();
    if (requestData) isPending = true;
  }

  return mapGroup(data, membership, isPending);
}

export async function getGroupBySlug(slug: string): Promise<Group | null> {
  if (!slug) {
    console.error('getGroupBySlug: Slug is required');
    return null;
  }

  const { data } = await getSupabaseClient().from('groups').select('id').eq('slug', slug).maybeSingle();
  if (!data) return null;
  return getGroupById(data.id);
}

export async function createGroup(input: CreateGroupInput): Promise<Group> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');
  const supabase = getSupabaseClient();

  // Validation
  if (!input.name?.trim()) {
    throw new Error('Group name is required');
  }
  if (input.name.length > 50) {
    throw new Error('Group name must be 50 characters or less');
  }
  if (!input.description?.trim()) {
    throw new Error('Description is required');
  }
  if (input.description.length > 500) {
    throw new Error('Description must be 500 characters or less');
  }
  if (!input.categoryId) {
    throw new Error('Category is required');
  }
  if (!input.icon) {
    throw new Error('Icon is required');
  }

  const slug = input.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const { data, error } = await supabase
    .from('groups')
    .insert({
      owner_id: userId,
      name: input.name.trim(),
      slug,
      description: input.description.trim(),
      icon: input.icon,
      category_id: input.categoryId,
      is_private: input.isPrivate,
      rules: input.rules?.trim() || null,
    })
    .select(`
      *,
      profiles!owner_id(*),
      categories!category_id(*)
    `)
    .single();

  if (error) {
    if (error.code === '23505') throw new Error('A group with this name already exists');
    throw new Error(error.message);
  }

  // Add owner as member
  await getSupabaseClient().from('group_members').insert({ group_id: data.id, user_id: userId, role: 'owner' });

  return mapGroup(data, { role: 'owner' });
}

export async function joinGroup(groupId: string): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  if (!groupId) {
    throw new Error('Group ID is required');
  }

  const group = await getGroupById(groupId);
  if (!group) throw new Error('Group not found');

  if (group.isMember) {
    throw new Error('You are already a member of this group');
  }

  if (group.isPrivate) {
    const { error } = await getSupabaseClient().from('group_requests').insert({ group_id: groupId, user_id: userId });
    if (error && error.code !== '23505') throw new Error(error.message);
  } else {
    const { error } = await getSupabaseClient().from('group_members').insert({ group_id: groupId, user_id: userId, role: 'member' });
    if (error) throw new Error(error.message);
  }
}

export async function leaveGroup(groupId: string): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  if (!groupId) {
    throw new Error('Group ID is required');
  }

  // Check if user is owner
  const { data: membership } = await getSupabaseClient()
    .from('group_members')
    .select('role')
    .eq('group_id', groupId)
    .eq('user_id', userId)
    .maybeSingle();

  if (membership?.role === 'owner') {
    throw new Error('Owners cannot leave their group. Transfer ownership first.');
  }

  const { error } = await getSupabaseClient().from('group_members').delete().eq('group_id', groupId).eq('user_id', userId);
  if (error) throw new Error(error.message);
}

export async function getGroupMembers(groupId: string): Promise<GroupMember[]> {
  if (!groupId) {
    console.error('getGroupMembers: Group ID is required');
    return [];
  }

  const { data, error } = await getSupabaseClient()
    .from('group_members')
    .select(`
      *,
      profiles!user_id(*)
    `)
    .eq('group_id', groupId)
    .order('joined_at', { ascending: true });

  if (error) throw new Error(error.message);

  return (data || []).map(m => ({
    id: m.id,
    user: m.profiles ? mapProfile(m.profiles) : ({} as User),
    role: m.role,
    joinedAt: m.joined_at,
  }));
}

// Fetch pending membership requests for a group (owner/admin only in UI)
export async function getGroupRequests(groupId: string): Promise<{ id: string; user: User; createdAt: string }[]> {
  if (!groupId) {
    console.error('getGroupRequests: Group ID is required');
    return [];
  }

  const { data, error } = await getSupabaseClient()
    .from('group_requests')
    .select('id, created_at, profiles!user_id(*)')
    .eq('group_id', groupId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('getGroupRequests error:', error);
    return [];
  }

  return (data || []).map((r: any) => ({ id: r.id, user: r.profiles ? mapProfile(r.profiles) : ({} as User), createdAt: r.created_at }));
}

// Approve a pending request and add the user as a member
export async function approveGroupRequest(groupId: string, requestId: string): Promise<GroupMember> {
  const currentUserId = await getCurrentUserId();
  if (!currentUserId) throw new Error('Not authenticated');
  if (!groupId || !requestId) throw new Error('Group ID and request ID are required');

  // Ensure current user is the owner of the group
  const { data: membership } = await getSupabaseClient()
    .from('group_members')
    .select('role')
    .eq('group_id', groupId)
    .eq('user_id', currentUserId)
    .single();

  if (!membership || membership.role !== 'owner') {
    throw new Error('Only the group owner can approve requests');
  }

  // Fetch the request to get the requester user_id
  const { data: reqData, error: reqErr } = await getSupabaseClient()
    .from('group_requests')
    .select('user_id')
    .eq('id', requestId)
    .eq('group_id', groupId)
    .single();

  if (reqErr || !reqData) throw new Error('Request not found');
  console.log("request id", reqData);
  const requesterId = reqData.user_id;

  // Add to members
  const { data: insertData, error: insertErr } = await getSupabaseClient()
    .from('group_members')
    .insert({ group_id: groupId, user_id: requesterId, role: 'member' })
    .select('id, joined_at')
    .single();

  if (insertErr) throw new Error(insertErr.message);

  console.log("deleting group, user", groupId, requesterId);
  // Remove the request
  // Remove all requests for this user in this group (cleanup duplicates)
  const { error: delErr } = await getSupabaseClient()
    .from('group_requests')
    .delete()
    .eq('group_id', groupId)
    .eq('user_id', requesterId);

  if (delErr) console.warn('Failed to remove group request(s) after approving:', delErr.message);

  // Fetch user profile
  const user = await getUserById(requesterId);
  if (!user) throw new Error('User not found');

  return {
    id: insertData.id,
    user,
    role: 'member',
    joinedAt: insertData.joined_at || new Date().toISOString(),
  };
}

export async function getGroupDecisions(groupId: string): Promise<Decision[]> {
  if (!groupId) {
    console.error('getGroupDecisions: Group ID is required');
    return [];
  }

  return getDecisions({ groupId });
}

// ============================================
// POLL SERVICE
// ============================================
export async function getPolls(options?: { limit?: number }): Promise<Poll[]> {
  const supabase = getSupabaseClient();
  const userId = await getCurrentUserId();

  const { data, error } = await supabase
    .from('polls')
    .select(`
      *,
      profiles!user_id(*),
      categories!category_id(*),
      poll_options(*)
    `)
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(options?.limit || 20);

  if (error) throw new Error(error.message);

  console.log('[Decixa][getPolls] fetched:', data?.length ?? 0, 'userId:', userId);

  // Hide content from users the viewer has blocked
  const blockedIds = await getBlockedUserIds(userId);
  const visible = (data || []).filter(p => !blockedIds.has(p.user_id));
  console.log('[Decixa][getPolls] blocked:', blockedIds.size, 'visible:', visible.length);

  // Fetch user's votes for all polls
  const userVotesMap: Record<string, string[]> = {};
  const bookmarkedPollIds: Set<string> = new Set();
  if (userId && visible.length > 0) {
    const pollIds = visible.map(p => p.id);
    const [votesResult, bookmarksResult] = await Promise.all([
      supabase
        .from('poll_votes')
        .select('poll_id, option_id')
        .eq('user_id', userId)
        .in('poll_id', pollIds),
      supabase
        .from('bookmarks')
        .select('poll_id')
        .eq('user_id', userId)
        .in('poll_id', pollIds),
    ]);

    if (votesResult.data) {
      votesResult.data.forEach((v: any) => {
        if (!userVotesMap[v.poll_id]) userVotesMap[v.poll_id] = [];
        userVotesMap[v.poll_id]!.push(v.option_id);
      });
    }

    if (bookmarksResult.data) {
      bookmarksResult.data.forEach((b: any) => {
        if (b.poll_id) bookmarkedPollIds.add(b.poll_id);
      });
    }
  }

  return visible.map(p => ({
    id: p.id,
    userId: p.user_id,
    question: p.title,
    description: p.description,
    category: p.categories ? mapCategory(p.categories) : undefined,
    // profiles can be null for orphaned content (author's profile deleted);
    // an unguarded mapProfile(null) here crashed the entire feed.
    user: p.profiles ? mapProfile(p.profiles) : ({} as User),
    isAnonymous: p.is_anonymous,
    allowMultiple: p.allow_multiple,
    options: (p.poll_options || []).map((o: any) => ({
      id: o.id,
      text: o.text,
      votesCount: o.votes_count || 0,
      percentage: p.total_votes > 0 ? Math.round((o.votes_count / p.total_votes) * 100) : 0,
    })),
    totalVotes: p.total_votes || 0,
    expiresAt: p.expires_at || undefined,
    createdAt: p.created_at,
    group: undefined,
    aiInsight: undefined,
    userVotedOptionIds: userVotesMap[p.id] || [],
    isBookmarked: bookmarkedPollIds.has(p.id),
  }));
}

export async function getPollById(id: string): Promise<Poll | null> {
  if (!id) {
    console.error('getPollById: ID is required');
    return null;
  }

  const supabase = getSupabaseClient();
  const userId = await getCurrentUserId();

  const { data, error } = await supabase
    .from('polls')
    .select(`
      *,
      profiles!user_id(*),
      categories!category_id(*),
      poll_options(*)
    `)
    .eq('id', id)
    .single();

  if (error || !data) return null;

  // Fetch user's votes for this poll
  let userVotedOptionIds: string[] = [];
  let isBookmarked = false;
  if (userId) {
    const [votesResult, bookmarkResult] = await Promise.all([
      supabase
        .from('poll_votes')
        .select('option_id')
        .eq('user_id', userId)
        .eq('poll_id', id),
      supabase
        .from('bookmarks')
        .select('id')
        .eq('user_id', userId)
        .eq('poll_id', id)
        .maybeSingle(),
    ]);

    if (votesResult.data) {
      userVotedOptionIds = votesResult.data.map((v: any) => v.option_id);
    }

    isBookmarked = !!bookmarkResult.data;
  }
  return {
    userId: data.user_id,
    id: data.id,
    question: data.title,
    description: data.description,
    category: data.categories ? mapCategory(data.categories) : undefined,
    user: data.profiles ? mapProfile(data.profiles) : ({} as User),
    isAnonymous: data.is_anonymous,
    allowMultiple: data.allow_multiple,
    options: (data.poll_options || []).map((o: any) => ({
      id: o.id,
      text: o.text,
      votesCount: o.votes_count || 0,
      percentage: data.total_votes > 0 ? Math.round((o.votes_count / data.total_votes) * 100) : 0,
    })),
    totalVotes: data.total_votes || 0,
    expiresAt: data.expires_at || undefined,
    createdAt: data.created_at,
    userVotedOptionIds,
    isBookmarked,
  };
}

export async function voteOnPoll(pollId: string, optionIds: string[]): Promise<Poll> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  if (!pollId) {
    throw new Error('Poll ID is required');
  }
  if (!optionIds || optionIds.length === 0) {
    throw new Error('At least one option must be selected');
  }

  // Insert new votes
  const votes = optionIds.map(optionId => ({
    poll_id: pollId,
    option_id: optionId,
    user_id: userId,
  }));

  const { error } = await getSupabaseClient().from('poll_votes').insert(votes);
  if (error) throw new Error(error.message);

  const poll = await getPollById(pollId);
  if (!poll) throw new Error('Poll not found');
  return poll;
}

export async function createPoll(input: CreatePollInput): Promise<Poll> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  const supabase = getSupabaseClient();

  // Validate options
  const filledOptions = input.options.filter(o => o.trim());
  if (filledOptions.length < 2) {
    throw new Error('At least 2 options are required');
  }
  if (filledOptions.length > 6) {
    throw new Error('Maximum 6 options allowed');
  }

  // Create the poll
  const { data: pollData, error: pollError } = await supabase
    .from('polls')
    .insert({
      user_id: userId,
      title: input.question.trim(),
      description: input.description?.trim() || null,
      category_id: input.categoryId,
      allow_multiple: input.allowMultiple || false,
      is_anonymous: input.isAnonymous || false,
      is_active: true,
      total_votes: 0,
      expires_at: input.expiresAt || null,
    })
    .select()
    .single();

  if (pollError) throw new Error(pollError.message);

  // Create poll options
  const optionsToInsert = filledOptions.map((text, index) => ({
    poll_id: pollData.id,
    text: text.trim(),
    votes_count: 0,
    sort_order: index,
  }));

  const { error: optionsError } = await supabase
    .from('poll_options')
    .insert(optionsToInsert);

  if (optionsError) throw new Error(optionsError.message);

  // If group IDs provided, link poll to groups
  if (input.groupIds && input.groupIds.length > 0) {
    const groupLinks = input.groupIds.map(groupId => ({
      poll_id: pollData.id,
      group_id: groupId,
    }));

    await supabase.from('poll_groups').insert(groupLinks);
  }

  // Fetch and return the complete poll
  const poll = await getPollById(pollData.id);
  if (!poll) throw new Error('Failed to retrieve created poll');

  return poll;
}

// ============================================
// FEED SERVICE
// ============================================
export async function getFeed(options?: {
  tab?: 'for-you' | 'following' | 'trending';
  categoryId?: string;
  limit?: number;
  offset?: number;
}): Promise<FeedItem[]> {
  console.log('[Decixa][getFeed] start', options);
  // Fetch both decisions and polls in parallel. Tolerate one half failing —
  // a bad poll row shouldn't blank the whole feed — but if BOTH fail,
  // surface the error so the UI shows its failure state instead of a
  // misleading "no content yet" empty feed.
  const [decisionsResult, pollsResult] = await Promise.allSettled([
    getDecisions({
      categoryId: options?.categoryId,
      limit: options?.limit,
      offset: options?.offset,
    }),
    getPolls({ limit: options?.limit }),
  ]);

  if (decisionsResult.status === 'rejected') {
    console.error('[Decixa][getFeed] decisions failed:', decisionsResult.reason);
  }
  if (pollsResult.status === 'rejected') {
    console.error('[Decixa][getFeed] polls failed:', pollsResult.reason);
  }
  if (decisionsResult.status === 'rejected' && pollsResult.status === 'rejected') {
    throw decisionsResult.reason;
  }

  const decisions = decisionsResult.status === 'fulfilled' ? decisionsResult.value : [];
  const polls = pollsResult.status === 'fulfilled' ? pollsResult.value : [];
  console.log('[Decixa][getFeed] decisions:', decisions.length, 'polls:', polls.length);

  // Combine into feed items
  const decisionItems: FeedItem[] = decisions.map(d => ({ type: 'decision' as const, data: d }));
  const pollItems: FeedItem[] = polls.map(p => ({ type: 'poll' as const, data: p }));

  const feed = [...decisionItems, ...pollItems];

  // Sort by created date (newest first)
  feed.sort((a, b) => {
    const dateA = new Date(a.data.createdAt).getTime();
    const dateB = new Date(b.data.createdAt).getTime();
    return dateB - dateA;
  });

  // Apply overall limit
  const limit = options?.limit || 20;
  return feed.slice(0, limit);
}

// ============================================
// USER SERVICE
// ============================================
export async function getUserById(id: string): Promise<User | null> {
  if (!id) {
    console.error('getUserById: ID is required');
    return null;
  }

  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', id)
    .single();

  if (error || !data) return null;
  return mapProfile(data);
}

export async function getCurrentUser(): Promise<User | null> {
  const userId = await getCurrentUserId();
  if (!userId) return null;
  return getUserById(userId);
}

// ============================================
// SEARCH SERVICE
// ============================================
export async function search(query: string): Promise<{
  decisions: Decision[];
  groups: Group[];
  users: User[];
}> {
  if (!query?.trim()) {
    return { decisions: [], groups: [], users: [] };
  }

  const supabase = getSupabaseClient();
  const q = query.trim();

  const userId = await getCurrentUserId();
  const [decisionsRes, groupsRes, usersRes] = await Promise.all([
    supabase
      .from('decisions')
      .select(`*, profiles!user_id(*), categories!category_id(*)`)
      .or(`title.ilike.%${q}%,description.ilike.%${q}%`)
      .limit(5),
    supabase
      .from('groups')
      .select(`*, profiles!owner_id(*), categories!category_id(*)`)
      .or(`name.ilike.%${q}%,description.ilike.%${q}%`)
      .limit(5),
    supabase
      .from('profiles')
      .select('*')
      .or(`display_name.ilike.%${q}%,username.ilike.%${q}%`)
      .limit(5),
  ]);

  // For groups returned in search, fetch current user's membership and pending requests if logged in
  const groupsData = (groupsRes.data || []);
  const groupMemberships: Record<string, any> = {};
  const pendingRequests: Record<string, boolean> = {};
  if (userId && groupsData.length > 0) {
    const groupIds = groupsData.map((g: any) => g.id);
    const [{ data: memberData }, { data: reqData }] = await Promise.all([
      supabase.from('group_members').select('group_id, role').eq('user_id', userId).in('group_id', groupIds),
      supabase.from('group_requests').select('group_id').eq('user_id', userId).in('group_id', groupIds),
    ]);
    memberData?.forEach((m: any) => { groupMemberships[m.group_id] = m; });
    reqData?.forEach((r: any) => { pendingRequests[r.group_id] = true; });
  }

  return {
    decisions: (decisionsRes.data || []).map(d => mapDecision(d)),
    groups: groupsData.map((g: any) => mapGroup(g, groupMemberships[g.id], pendingRequests[g.id])),
    users: (usersRes.data || []).map(mapProfile),
  };
}

export async function getTrending(): Promise<Decision[]> {
  const supabase = getSupabaseClient();

  const { data } = await supabase
    .from('decisions')
    .select(`*, profiles!user_id(*), categories!category_id(*)`)
    .eq('is_active', true)
    .order('total_votes', { ascending: false })
    .limit(5);

  const blockedIds = await getBlockedUserIds(await getCurrentUserId());
  return (data || [])
    .filter(d => !blockedIds.has(d.user_id))
    .map(d => mapDecision(d));
}

// ============================================
// CATEGORY SERVICE
// ============================================
export async function getCategories(): Promise<Category[]> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw new Error(error.message);
  return (data || []).map(mapCategory);
}

// ============================================
// AI CHAT (placeholder - needs edge function)
// ============================================
export async function getAIConversations() {
  const userId = await getCurrentUserId();
  if (!userId) return [];

  const { data, error } = await getSupabaseClient()
    .from('ai_conversations')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('getAIConversations error:', error);
    return [];
  }

  return data || [];
}

export async function getAIConversationById(_id: string) {
  if (!_id) return null;
  const { data, error } = await getSupabaseClient().from('ai_conversations').select('*').eq('id', _id).single();
  if (error || !data) return null;
  return data;
}

export async function getAIConversationCount(): Promise<number> {
  console.log("count fetch start");
  const userId = await getCurrentUserId();
  if (!userId) return 0;

  const { count, error } = await getSupabaseClient()
    .from('ai_conversations')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId);

  console.log("fetch done", count, error, userId);


  if (error) {
    console.error('getAIConversationCount error:', error);
    return 0;
  }

  return count || 0;
}

export async function sendAIMessage(message: string) {

  // Get session (may be null if not authenticated)
  const session = await getCurrentSession();

  // Prefer the env-provided anon key
  const anon = env.supabaseAnonKey || process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY;
  if (!anon) {
    console.log('Supabase anon key is missing');
    throw new Error('Configuration error. Please try again later.');
  }

  const sessiontoken = session?.access_token || '';
  console.log("tokens", anon, sessiontoken);
  // Call edge function 'ask-decixa'
  const { data, error } = await getSupabaseClient().functions.invoke('ask-decixa', {
    body: { question: message },
    headers: {
      Authorization: `Bearer ${anon}`,
      userID: sessiontoken,
    },
  });
  console.log("ai success", data);
  if (error) {
    console.error('ask-decixa function error:', error);
    throw new Error(error.message || 'AI function error');
  }

  // The edge function may return a wrapped payload like { decision: {...}, remaining: N }
  const payload: any = data || {};
  const ai = payload.decision || payload;

  // Normalize fields to the UI DecisionAnalysis shape to avoid breaking the UI
  const mapped = {
    recommendation: ai.decision || ai.recommendation || '',
    explanation: ai.description || ai.explanation || '',
    impacts: {} as { [key: string]: string | undefined },
    dependencies: ai.dependencies || ai.deps || [],
    riskFactors: ai.risk_factors || ai.riskFactors || [],
    pros: ai.pros || ai.advantages || [],
    cons: ai.cons || ai.disadvantages || [],
    analysis: {
      advantageScore: ai.advantage_score ?? ai.analysis?.advantageScore ?? 0,
      sentiment: ai.sentiment || ai.analysis?.sentiment || '',
      successRate: typeof ai.success_rate === 'string' ? Number(ai.success_rate) || 0 : (ai.success_rate ?? ai.analysis?.successRate ?? 0),
      failureRate: typeof ai.failure_rate === 'string' ? Number(ai.failure_rate) || 0 : (ai.failure_rate ?? ai.analysis?.failureRate ?? 0),
    },
    financialImpacts: ai.financial_impact || ai.financialImpacts || [],
    estimatedTimeFrame: ai.timeframe || ai.estimatedTimeFrame || ai.estimated_timeframe || '',
  } as any;

  // If impacts is an array in the payload, convert to a keyed object to keep UI stable
  const impactsArray = ai.impacts || ai.impact || [];
  if (Array.isArray(impactsArray)) {
    impactsArray.forEach((val: string, idx: number) => {
      mapped.impacts[`Impact ${idx + 1}`] = val;
    });
  } else if (typeof ai.impacts === 'object' && ai.impacts !== null) {
    mapped.impacts = ai.impacts;
  }

  // Persist the conversation (if we have an authenticated user)
  try {
    const userIdForInsert = await getCurrentUserId();
    if (userIdForInsert) {
      const title = (message || '').trim().slice(0, 500) || 'AI Conversation';
      const { data: convData, error: convError } = await getSupabaseClient()
        .from('ai_conversations')
        .insert({ user_id: userIdForInsert, title })
        .select()
        .single();

      if (convError) {
        console.warn('Failed to insert ai_conversation:', convError);
      } else {
        // Attach conversation info to payload for callers who want it
        payload.conversation = convData;
      }
    }
  } catch (e) {
    console.warn('ai conversation insert failed:', e);
  }

  return { analysis: mapped, remaining: payload.remaining, conversation: payload.conversation };
}

// ============================================
// NOTIFICATION SERVICE
// ============================================
export async function getNotifications(): Promise<Notification[]> {
  const userId = await getCurrentUserId();
  if (!userId) return [];

  const { data, error } = await getSupabaseClient()
    .from('notifications')
    .select(`
      *,
      actor:profiles!actor_id(*),
      decision:decisions!decision_id(id, title, description),
      poll:polls!poll_id(id, title),
      group:groups!group_id(id, name, icon)
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.log("get notification error", error);
    throw new Error(error.message);
  }

  return (data || []).map(n => ({
    id: n.id,
    type: n.type as NotificationType,
    title: n.title,
    message: n.message || '',
    isRead: n.is_read,
    actor: n.actor ? mapProfile(n.actor) : undefined,
    decision: n.decision ? { id: n.decision.id, title: n.decision.title } as Decision : undefined,
    poll: n.poll ? { id: n.poll.id, question: n.poll.question } as Poll : undefined,
    group: n.group ? { id: n.group.id, name: n.group.name, icon: n.group.icon } as Group : undefined,
    createdAt: n.created_at,
  }));
}

export async function getUnreadNotificationCount(): Promise<number> {
  const userId = await getCurrentUserId();
  if (!userId) return 0;

  const { count, error } = await getSupabaseClient()
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_read', false);

  if (error) throw new Error(error.message);
  return count || 0;
}

export async function markNotificationAsRead(notificationId: string): Promise<Notification> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  const { data, error } = await getSupabaseClient()
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return {
    id: data.id,
    type: data.type,
    title: data.title,
    message: data.message || '',
    isRead: data.is_read,
    createdAt: data.created_at,
  };
}

export async function markAllNotificationsAsRead(): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  const { error } = await getSupabaseClient()
    .from('notifications')
    .update({ is_read: true })
    .eq('user_id', userId)
    .eq('is_read', false);

  if (error) throw new Error(error.message);
}

export async function deleteNotification(notificationId: string): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  const { error } = await getSupabaseClient()
    .from('notifications')
    .delete()
    .eq('id', notificationId)
    .eq('user_id', userId);

  if (error) throw new Error(error.message);
}

export async function clearAllNotifications(): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  const { error } = await getSupabaseClient()
    .from('notifications')
    .delete()
    .eq('user_id', userId);

  if (error) throw new Error(error.message);
}

// ============================================
// SETTINGS SERVICE
// ============================================
interface UserSettingsUpdate {
  push_notifications?: boolean;
  email_notifications?: boolean;
  is_private?: boolean;
  show_online_status?: boolean;
  allow_anonymous?: boolean;
}

export async function updateUserSettings(settings: UserSettingsUpdate): Promise<User> {
  const supabase = getSupabaseClient();

  const { data: { user: authUser } } = await supabase.auth.getUser();
  if (!authUser) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('profiles')
    .update({
      ...settings,
      updated_at: new Date().toISOString(),
    })
    .eq('id', authUser.id)
    .select()
    .single();

  if (error) {
    console.error('Error updating settings:', error);
    throw error;
  }

  return data as User;
}

export async function logoutUser(): Promise<void> {
  const supabase = getSupabaseClient();

  const { error } = await supabase.auth.signOut();

  if (error) {
    console.error('Error logging out:', error);
    throw error;
  }
}

export async function deleteUserAccount(): Promise<void> {
  const supabase = getSupabaseClient();

  // Get current session to obtain access token for edge function auth.
  // The delete-account edge function validates the `userToken` header via
  // the admin client, so Authorization only needs to pass the gateway.
  const session = await getCurrentSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error('Not authenticated');

  const anonKey = env.supabaseAnonKey;
  if (!anonKey) throw new Error('Supabase anon key not configured');
  const { error } = await supabase.functions.invoke('delete-account', {
    body: { name: 'Functions' },
    headers: {
      Authorization: `Bearer ${anonKey}`,
      userToken: accessToken,
    },
  });

  if (error) {
    console.error('Edge function error:', error);
    // FunctionsHttpError carries the edge function's response in `context` —
    // surface its message instead of "[object Object]".
    let message = 'Failed to delete account. Please try again.';
    try {
      const body = await (error as any).context?.json?.();
      message = body?.msg || body?.error || message;
    } catch { /* response body wasn't JSON */ }
    throw new Error(message);
  }

  // Attempt to sign out the local client. This usually fails because the
  // user record was just deleted; we still clear all local state below.
  try {
    await supabase.auth.signOut();
  } catch (signOutError) {
    console.warn('Sign out failed after account deletion (user likely removed):', signOutError);
  }

  // Clear all local app data so nothing from the deleted account survives.
  try { localStorage.clear(); } catch (e) { console.warn('Failed to clear localStorage:', e); }
  try { sessionStorage.clear(); } catch (e) { console.warn('Failed to clear sessionStorage:', e); }
  queryClient.clear();

  // Reset auth store to unauthenticated state without calling its logout
  // (which would call signOut again). Navigation is the caller's job.
  useAuthStore.setState({ user: null, isAuthenticated: false, isLoading: false, error: null });
}

export async function getBlockedUsers(): Promise<User[]> {
  const supabase = getSupabaseClient();

  const { data: { user: authUser } } = await supabase.auth.getUser();
  if (!authUser) return [];

  const { data, error } = await supabase
    .from('blocks')
    .select(`
      blocked:profiles!blocks_blocked_id_fkey (
        id,
        email,
        username,
        display_name,
        avatar_url,
        bio,
        is_verified,
        created_at
      )
    `)
    .eq('blocker_id', authUser.id);

  if (error) {
    console.error('Error fetching blocked users:', error);
    return [];
  }

  return data?.map(item => item.blocked as unknown as User) || [];
}

export async function blockUser(userId: string): Promise<void> {
  const supabase = getSupabaseClient();

  const { data: { user: authUser } } = await supabase.auth.getUser();
  if (!authUser) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('blocks')
    .insert({
      blocker_id: authUser.id,
      blocked_id: userId,
    });

  if (error) {
    console.error('Error blocking user:', error);
    throw error;
  }

  invalidateBlockedIdsCache();
  // Drop cached feeds/comments so the blocked user's content disappears
  // without waiting for staleTime to expire.
  queryClient.clear();
}

export async function unblockUser(userId: string): Promise<void> {
  const supabase = getSupabaseClient();

  const { data: { user: authUser } } = await supabase.auth.getUser();
  if (!authUser) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('blocks')
    .delete()
    .eq('blocker_id', authUser.id)
    .eq('blocked_id', userId);

  if (error) {
    console.error('Error unblocking user:', error);
    throw error;
  }

  invalidateBlockedIdsCache();
  queryClient.clear();
}
/* ============================================================================
   EXPLORE SERVICE FUNCTIONS - SUPABASE
   ============================================================================ */

/**
 * Get trending decisions (sorted by total votes)
 */
export async function getTrendingDecisions(options?: { limit?: number }): Promise<Decision[]> {
  const supabase = getSupabaseClient();
  const limit = options?.limit || 10;

  const { data, error } = await supabase
    .from('decisions')
    .select(`
      *,
      profiles!user_id(*),
      categories!category_id(*)
    `)
    .eq('is_active', true)
    .order('total_votes', { ascending: false })
    .limit(limit);

  if (error) throw new Error(error.message);

  const blockedIds = await getBlockedUserIds(await getCurrentUserId());
  return (data || [])
    .filter(d => !blockedIds.has(d.user_id))
    .map(d => mapDecision(d));
}

/**
 * Get popular groups (sorted by member count)
 */
export async function getPopularGroups(options?: { limit?: number }): Promise<Group[]> {
  const supabase = getSupabaseClient();
  const userId = await getCurrentUserId();
  const limit = options?.limit || 10;

  const { data, error } = await supabase
    .from('groups')
    .select(`
      *,
      profiles!owner_id(*),
      categories!category_id(*)
    `)
    .eq('is_active', true)
    .order('members_count', { ascending: false })
    .limit(limit);

  if (error) throw new Error(error.message);

  // Get memberships if user is logged in
  const memberships: Record<string, any> = {};
  if (userId && data) {
    const groupIds = data.map(g => g.id);
    const { data: memberData } = await supabase
      .from('group_members')
      .select('group_id, role')
      .eq('user_id', userId)
      .in('group_id', groupIds);

    memberData?.forEach(m => { memberships[m.group_id] = m; });
  }

  // Also fetch pending requests for current user to mark pending groups
  const pendingRequests: Record<string, boolean> = {};
  if (userId && data) {
    const groupIds = data.map(g => g.id);
    const { data: reqData } = await supabase
      .from('group_requests')
      .select('group_id')
      .eq('user_id', userId)
      .in('group_id', groupIds);
    reqData?.forEach((r: any) => { pendingRequests[r.group_id] = true; });
  }

  return (data || []).map(g => mapGroup(g, memberships[g.id], pendingRequests[g.id]));
}

/**
 * Get top contributors (sorted by impact score)
 */
export async function getTopContributors(options?: { limit?: number }): Promise<User[]> {
  const supabase = getSupabaseClient();
  const limit = options?.limit || 10;

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('is_private', false)
    .order('impact_score', { ascending: false })
    .limit(limit);

  if (error) throw new Error(error.message);

  return (data || []).map(mapProfile);
}

/**
 * Return comment IDs from `comment_helpful` marked by a specific user.
 * Used to determine which comments were marked helpful by the decision author.
 */
export async function getHelpfulMarksForComments(commentIds: string[], userId: string): Promise<string[]> {
  if (!commentIds || commentIds.length === 0) return [];
  if (!userId) return [];

  const { data, error } = await getSupabaseClient()
    .from('comment_helpful')
    .select('comment_id')
    .in('comment_id', [...commentIds])
    .eq('user_id', userId);

  if (error) {
    console.log("error helpful cmt fetch", error);
    throw new Error(error.message);
  }

  return (data || []).map((r: any) => r.comment_id);
}

// Get pending/sent invites for a group
export async function getGroupInvites(groupId: string): Promise<{ email: string; createdAt: string }[]> {
  if (!groupId) {
    console.error('getGroupInvites: Group ID is required');
    return [];
  }

  const { data, error } = await getSupabaseClient()
    .from('group_invites')
    .select('invited_email, created_at')
    .eq('group_id', groupId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('getGroupInvites error:', error);
    return [];
  }

  return (data || []).map((r: any) => ({ email: r.invited_email, createdAt: r.created_at }));
}

/**
 * Invite a user to a group by email (sends email via Resend)
 */
export async function inviteByEmail(groupId: string, email: string): Promise<{ message: string }> {
  const supabase = getSupabaseClient();

  // Refresh session to ensure token is valid
  const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
  if (refreshError || !session) {
    throw new Error('Not authenticated — please log in again');
  }

  const normalizedEmail = (email || '').trim().toLowerCase();
  if (!normalizedEmail) throw new Error('Email is required');

  // Prevent self-invite
  if (session.user.email && normalizedEmail === session.user.email.toLowerCase()) {
    throw new Error('You cannot invite yourself to the group');
  }

  // Only group owner can send invites
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  const { data: groupInfo, error: groupInfoError } = await supabase
    .from('groups')
    .select('owner_id')
    .eq('id', groupId)
    .maybeSingle();

  if (groupInfoError) throw groupInfoError;
  if (!groupInfo) throw new Error('Group not found');
  if (groupInfo.owner_id !== userId) throw new Error('Not authorized to invite users');

  // Call edge function to send invite email

  const { data, error } = await supabase.functions.invoke('send-group-invite', {
    body: { group_id: groupId, email: normalizedEmail },
  });

  if (error) {
    console.error('inviteByEmail edge function error:', error);
    throw new Error(error.message || 'Failed to send invite');
  }

  if (!data) throw new Error('No response from server');
  if ((data as any).error) throw new Error((data as any).error || 'Failed to send invite');

  return { message: (data as any).message || 'Invite sent' };
}

export interface AcceptInviteResult {
  success: boolean;
  message?: string;
  group_id?: string;
  group_name?: string;
  group_icon?: string;
  already_member?: boolean;
}

/**
 * Accept a group invite using an invite token by calling the edge function.
 * The accept-group-invite function authenticates via the Authorization
 * header, which functions.invoke fills with the current session's JWT —
 * don't override it (the anon key would fail the function's getUser check).
 */
export async function acceptGroupInvite(token: string): Promise<AcceptInviteResult> {
  if (!token) throw new Error('Invite token is required');

  const supabase = getSupabaseClient();

  const { data, error } = await supabase.functions.invoke('accept-group-invite', {
    body: { token },
  });

  if (error) {
    console.error('acceptGroupInvite edge function error:', error);
    let message = 'Failed to accept invite';
    try {
      const body = await (error as any).context?.json?.();
      if (body?.error) message = body.error;
    } catch { /* response body wasn't JSON */ }
    throw new Error(message);
  }

  if (!data) throw new Error('No response from server');
  if ((data as any).error) throw new Error((data as any).error || 'Failed to accept invite');

  return data as AcceptInviteResult;
}

// Remove a member from a group (owner or admin action)
export async function removeGroupMember(groupId: string, userIdToRemove: string): Promise<void> {
  const currentUserId = await getCurrentUserId();
  if (!currentUserId) throw new Error('Not authenticated');
  if (!groupId || !userIdToRemove) throw new Error('Group ID and user ID are required');

  // Check current user's role in the group
  const { data: membership } = await getSupabaseClient()
    .from('group_members')
    .select('role')
    .eq('group_id', groupId)
    .eq('user_id', currentUserId)
    .single();

  if (!membership) throw new Error('You are not a member of this group');
  if (membership.role !== 'owner' && membership.role !== 'admin') {
    throw new Error('Only owners or admins can remove members');
  }

  // Prevent removing owner via this endpoint
  const { data: targetMembership } = await getSupabaseClient()
    .from('group_members')
    .select('role')
    .eq('group_id', groupId)
    .eq('user_id', userIdToRemove)
    .single();

  if (!targetMembership) throw new Error('Target user is not a member');
  if (targetMembership.role === 'owner') throw new Error('Cannot remove the group owner');

  const { error } = await getSupabaseClient()
    .from('group_members')
    .delete()
    .eq('group_id', groupId)
    .eq('user_id', userIdToRemove);

  if (error) throw new Error(error.message);
}